import React from 'react';
import { Star, ExternalLink, Quote, Award } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { googleReviews, googleStats, companyInfo } from '../data/mock';

const Reviews = () => {
  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'
        }`}
      />
    ));
  };

  // Show only first 4 reviews for display
  const displayedReviews = googleReviews.slice(0, 4);

  return (
    <section id="avis" className="section-padding bg-gradient-to-br from-blue-50 via-white to-orange-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 right-20 w-72 h-72 bg-[hsl(207,90%,54%)] rounded-full opacity-5 blur-3xl" />
      <div className="absolute bottom-20 left-20 w-96 h-96 bg-[hsl(32,95%,55%)] rounded-full opacity-5 blur-3xl" />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[hsl(32,95%,55%)] font-semibold text-sm uppercase tracking-wider mb-4">
            Témoignages
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Ce que disent nos{' '}
            <span className="text-gradient">clients</span>
          </h2>
          
          {/* Google Rating Summary */}
          <div className="inline-flex items-center gap-4 bg-white rounded-2xl px-8 py-4 shadow-lg">
            <img 
              src="https://www.google.com/images/branding/googlelogo/2x/googlelogo_color_92x30dp.png" 
              alt="Google" 
              className="h-8 w-auto"
            />
            <div className="flex items-center gap-2">
              <span className="text-3xl font-bold text-gray-900">{googleStats.averageRating}</span>
              <div className="flex">{renderStars(Math.round(googleStats.averageRating))}</div>
            </div>
            <span className="text-gray-500">({googleStats.totalReviews} avis)</span>
          </div>
        </div>

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {displayedReviews.map((review, index) => (
            <Card 
              key={review.id} 
              className="group hover-lift border-0 shadow-lg hover:shadow-2xl bg-white relative overflow-hidden"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {/* Quote decoration */}
              <div className="absolute top-4 right-4 opacity-10">
                <Quote className="w-12 h-12 text-[hsl(207,90%,54%)]" />
              </div>
              
              <CardContent className="p-6">
                {/* Header with avatar and name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[hsl(207,90%,54%)] to-[hsl(207,90%,44%)] flex items-center justify-center text-white font-semibold text-sm shadow-md">
                    {review.avatar}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-gray-900">{review.name}</h4>
                      {review.isLocalGuide && (
                        <Award className="w-4 h-4 text-[hsl(32,95%,55%)]" title="Local Guide" />
                      )}
                    </div>
                    <p className="text-sm text-gray-500">{review.date}</p>
                  </div>
                </div>

                {/* Stars */}
                <div className="flex mb-3">
                  {renderStars(review.rating)}
                </div>

                {/* Review text */}
                <p className="text-gray-600 text-sm leading-relaxed line-clamp-4">
                  "{review.text}"
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* More reviews indicator */}
        <p className="text-center text-gray-500 mb-8">
          Et {googleStats.totalReviews - displayedReviews.length} autres avis positifs sur Google...
        </p>

        {/* CTA to Google Reviews */}
        <div className="text-center">
          <div className="inline-flex flex-col sm:flex-row items-center gap-4 bg-white rounded-2xl p-6 shadow-xl">
            <p className="text-gray-700 font-medium">
              Vous êtes satisfait de nos services ?
            </p>
            <Button
              asChild
              className="bg-[hsl(207,90%,54%)] hover:bg-[hsl(207,90%,44%)] text-white font-semibold px-6 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <a 
                href={companyInfo.googleLeaveReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="leave-google-review-btn"
                className="flex items-center gap-2"
              >
                <Star className="w-4 h-4" />
                Laissez-nous un avis Google
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;
